import * as React from 'react';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography
} from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { getUserPurposeStats } from '../Api';
import UserPurposeStatsDto from '../models/dtos/UserPurposeStatsDto';
import { userPurposeColor, userPurposeLabel } from '../models/userPurpose';

const PERIODS = [
    { value: 'Week', label: 'Last 7 days' },
    { value: 'Month', label: 'Last 30 days' },
    { value: 'ThreeMonths', label: 'Last 90 days' },
    { value: 'All', label: 'All time' },
];

interface UserPurposeCardProps {
    osType?: string,
}

/**
 * "Why do you play?" answers from the intro tour. A player who changes their answer is counted once,
 * under the latest answer, in the period of that change.
 */
export default function UserPurposeCard({ osType }: UserPurposeCardProps) {
    const [period, setPeriod] = React.useState('Month');
    const [stats, setStats] = React.useState<UserPurposeStatsDto | null>(null);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        let isActive = true;
        setLoading(true);
        getUserPurposeStats(period, osType).then(result => {
            if (!isActive) return;
            setStats(result);
            setLoading(false);
        });
        return () => {
            isActive = false;
        };
    }, [period, osType]);

    const rows = stats ? Object.keys(stats.counts).map(key => ({ key, value: stats.counts[key] })) : [];
    const data = rows
        .filter(row => row.value > 0)
        .map((row, index) => ({ id: index, value: row.value, label: userPurposeLabel(row.key), color: userPurposeColor(row.key) }));

    return (
        <Card sx={{ width: '100%' }} variant='outlined'>
            <CardContent>
                <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
                    <Typography variant="h5" component="div">Why players play</Typography>
                    <FormControl size='small' sx={{ minWidth: 160 }}>
                        <InputLabel id="user-purpose-period-label">Period</InputLabel>
                        <Select
                            labelId="user-purpose-period-label"
                            label="Period"
                            value={period}
                            onChange={(event) => setPeriod(event.target.value)}
                        >
                            {PERIODS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Stack>
                <Divider sx={{ my: 2 }} />
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                ) : stats == null ? (
                    <Typography color='error'>Could not load the purpose breakdown.</Typography>
                ) : stats.total === 0 ? (
                    <Typography color='text.secondary'>No answers in this period yet.</Typography>
                ) : (
                    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems='center'>
                        <PieChart
                            series={[{ data, innerRadius: 40, paddingAngle: 1, cornerRadius: 3 }]}
                            width={240}
                            height={220}
                            margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                            slotProps={{ legend: { hidden: true } }}
                        />
                        <Table size='small' sx={{ maxWidth: 420 }}>
                            <TableBody>
                                {rows.map(row => (
                                    <TableRow key={row.key}>
                                        <TableCell>
                                            <Box component='span' sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', mr: 1, bgcolor: userPurposeColor(row.key) }} />
                                            {userPurposeLabel(row.key)}
                                        </TableCell>
                                        <TableCell align='right'>{row.value.toLocaleString()}</TableCell>
                                        <TableCell align='right' sx={{ color: 'text.secondary' }}>
                                            {(row.value * 100 / stats.total).toFixed(1)}%
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                                    <TableCell align='right' sx={{ fontWeight: 600 }}>{stats.total.toLocaleString()}</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
}
